import { cn } from '@realtyfeed/utils';
import { forwardRef, useState } from 'react';

type InputPasswordToggleProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  invalid?: boolean;
  wrapperClassName?: string;
};

export const InputPasswordToggle = forwardRef<HTMLInputElement, InputPasswordToggleProps>(
  ({ label, invalid, className, wrapperClassName, id, placeholder, ...rest }, ref) => {
    const [visible, setVisible] = useState(false);

    return (
      <div className={wrapperClassName}>
        {label ? (
          <label
            htmlFor={id}
            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {label}
          </label>
        ) : null}
        <div
          className={cn(
            'flex h-control items-center rounded-lg border bg-slate-100 dark:bg-slate-900',
            invalid ? 'border-red-500' : 'border-slate-200 dark:border-slate-800'
          )}
        >
          <input
            ref={ref}
            id={id}
            type={visible ? 'text' : 'password'}
            placeholder={placeholder ?? '············'}
            className={cn(
              // `border-0`/`focus:ring-0` for the same reason as SelectField's
              // inner input: `@tailwindcss/forms` gives every bare input a 1px
              // border, which drew a second box inside this control's own border.
              'w-full rounded-lg border-0 bg-transparent px-3 py-2 text-sm text-slate-900 outline-none focus:ring-0 dark:text-white',
              className
            )}
            {...rest}
          />
          <button
            type="button"
            aria-label={visible ? 'Hide password' : 'Show password'}
            onClick={() => setVisible((prev) => !prev)}
            className="px-3 text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          >
            {visible ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.9 18.9 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <path d="M1 1l22 22" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  }
);

InputPasswordToggle.displayName = 'InputPasswordToggle';
